import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { api, STATUS_LABELS } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import PasswordPromptDialog from "@/components/PasswordPromptDialog";
import ShareBudgetDialog from "@/components/ShareBudgetDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FLOW = ["aguardando", "aprovado", "iniciando", "finalizado", "nota_fiscal_gerada", "nota_faturada"];

const Opt = ({ s, dir, onPick }) => (
  <button type="button" onClick={() => onPick(s, dir)} data-testid={`card-status-${dir}-${s}`}
    className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 text-left text-[12px] font-bold text-slate-700">
    {dir === "back" ? <ChevronLeft className="w-4 h-4 text-amber-600" /> : <ChevronRight className="w-4 h-4 text-emerald-600" />}
    <span className="flex-1">{dir === "back" ? "Voltar para" : "Avançar para"} <b className="text-slate-900">{STATUS_LABELS[s]}</b></span>
    {dir === "back" && <span className="text-[9px] uppercase text-amber-700 font-black">senha</span>}
  </button>
);

export default function CardStatusChanger({ b, onChanged }) {
  const idx = FLOW.indexOf(b.status);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [share, setShare] = useState(null);
  if (idx < 0 || b.parent_budget_id) return <StatusBadge status={b.status} />;
  const prev = FLOW[idx - 1];
  const next = FLOW[idx + 1];

  const pick = (s, dir) => { setOpen(false); setTarget({ s, dir }); };
  const change = async (s, password) => {
    try {
      await api.put(`/budgets/${b.id}`, password ? { status: s, password } : { status: s });
      toast.success("Status atualizado");
      setTarget(null);
      const full = await api.get(`/budgets/${b.id}`);
      if (full.data?.school) {
        setShare({ budget: full.data, msg: `Olá! O status do orçamento Nº ${b.number} foi atualizado para: *${STATUS_LABELS[s] || s}*.\n\nConsulte os detalhes no link abaixo.` });
      } else onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao mudar status");
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`card-status-trigger-${b.number}`} className="inline-flex items-center gap-0.5 rounded-full hover:ring-2 hover:ring-violet-200 transition-shadow">
            <StatusBadge status={b.status} /><ChevronDown className="w-3 h-3 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-1.5" data-testid={`card-status-menu-${b.number}`}>
          {next && <Opt s={next} dir="next" onPick={pick} />}
          {prev && <Opt s={prev} dir="back" onPick={pick} />}
        </PopoverContent>
      </Popover>

      <PasswordPromptDialog
        open={target?.dir === "back"}
        onOpenChange={(o) => { if (!o) setTarget(null); }}
        title="Retroceder status"
        description={`Para voltar o orçamento Nº ${b.number} de "${STATUS_LABELS[b.status]}" para "${STATUS_LABELS[target?.s] || ""}", informe a senha de administrador.`}
        onConfirm={(pwd) => change(target.s, pwd)}
      />

      <AlertDialog open={target?.dir === "next"} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <AlertDialogContent data-testid="card-status-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alteração de status</AlertDialogTitle>
            <AlertDialogDescription>
              Avançar o orçamento Nº {b.number} de <strong>{STATUS_LABELS[b.status]}</strong> para <strong>{STATUS_LABELS[target?.s] || ""}</strong>? A escola será notificada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="card-status-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => change(target.s)} data-testid="card-status-confirm-btn" className="bg-amber-600 hover:bg-amber-700 text-white">Sim, alterar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {share && (
        <ShareBudgetDialog open onOpenChange={(o) => { if (!o) { setShare(null); onChanged?.(); } }} budget={share.budget} school={share.budget.school}
          title="Avisar escola sobre novo status?" headlineLabel="Mensagem sobre mudança de status" extraMessage={share.msg} />
      )}
    </>
  );
}
